import {
  Button,
  CollapsibleSection,
  FOCUS_RING,
  InlineConfirmation,
  Section,
  cn,
  useTranslate,
  PANEL_SURFACE,
} from '@athena/ui'
import { useState } from 'react'

import { PRACTICE_CHATGPT_PROMPT } from './practiceChatGPTPrompt.js'

/**
 * The "write it with ChatGPT" helper on the create-practice screen.
 *
 * Nothing here talks to ChatGPT. The tutor copies a prompt and pastes it into
 * whatever assistant they already use; the practice still arrives in Athena as
 * Markdown, through the same parser, with the same validation. That is the
 * whole design: a copyable string is the smallest thing that helps, and it
 * adds no key, no service and no dependency on anyone else's uptime.
 *
 * The prompt is collapsed behind a disclosure because it is long and it is not
 * what the tutor came to this screen to read. Copy sits outside the disclosure
 * so the common case — copy, leave, paste — never involves opening it, and the
 * uncommon case, reading what is about to be pasted, is one click away. The
 * disclosure is `CollapsibleSection`, the same one the subject groups on the
 * practice lists use, so there is one expand/collapse pattern in the product
 * rather than one per screen.
 */
export function ChatGPTPromptPanel() {
  const t = useTranslate()
  const [copied, setCopied] = useState(false)

  async function onCopy() {
    await navigator.clipboard.writeText(PRACTICE_CHATGPT_PROMPT)
    setCopied(true)
  }

  return (
    <Section id="chatgpt-prompt" title={t('practice.chatgpt.title')}>
      <div className={cn(PANEL_SURFACE, 'flex flex-col gap-4')}>
        <p className="text-body-sm text-text-secondary">{t('practice.chatgpt.description')}</p>

        <CollapsibleSection title={t('practice.chatgpt.show')}>
          {/* Wraps rather than scrolling sideways, and caps its height rather
              than growing the page: a 200-line `pre` of long lines is the one
              thing on this screen that would push a phone sideways. Focusable
              and grouped, so the scroll region is reachable and named for
              anyone not using a mouse. */}
          <pre
            className={cn(
              'max-h-80 overflow-auto rounded-sm border border-border bg-surface-secondary px-4 py-3',
              'font-mono text-body-sm leading-6 whitespace-pre-wrap text-text',
              FOCUS_RING,
            )}
            role="group"
            tabIndex={0}
            aria-label={t('practice.chatgpt.promptLabel')}
          >
            {PRACTICE_CHATGPT_PROMPT}
          </pre>
        </CollapsibleSection>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              void onCopy()
            }}
          >
            {t('practice.chatgpt.copy')}
          </Button>
          {/* `role="status"` on the confirmation is what announces it, and the
              words are the confirmation — nothing here is carried by colour
              alone. */}
          {copied ? <InlineConfirmation>{t('practice.chatgpt.copied')}</InlineConfirmation> : null}
        </div>
      </div>
    </Section>
  )
}
